import React, { useState, useEffect, useRef, Fragment } from "react";
import { connect } from "react-redux";
import { HorizontalLinearStepper } from "@cdk-uip/react-stepper";
import { Button, Row, Col, Navbar, Card, Form } from "react-bootstrap";
import { omitBy, some } from "lodash";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { candidateActions } from "../../actions/candidateActions";
import PersonalInfo from "./PersonalInfo";
import QualificationInfo from "./QualificationInfo";
import OrganizationInfo from "./OrganizationInfo";
import AddressInfo from "./AddressInfo";
import Attachments from "./Attachments";
import api from "../../api/candidate.resource";
import {
  defaultToastOptions,
  CANDIDATE_STATUS,
  ROLES
} from "../../constants/constants";
import { b64toBlob, encrypt, imageCompressor } from "../../util/helpers";
import { Link } from "react-router-dom";
import { PageSpinner } from "@cdk-uip/react-page-spinner";

export const CandidateForm = ({
  generalDetails,
  personalDetails,
  qualificationDetails,
  addressDetails,
  organizationDetails,
  genders,
  maritalStatusList,
  states,
  getCandidateDetails,
  resetCandidateDetails,
  setCandidatePersonalDetails,
  addCandidateQualificationDetails,
  updateCandidateQualificationDetails,
  deleteCandidateQualificationDetails,
  updateCandidateAddressDetails,
  addCandidateAddressDetails,
  updateCandidateAddressDetailsBulk,
  updateCandidateOrganizationDetails,
  addCandidateOrganizationDetails,
  deleteCandidateOrganizationDetails,
  getGendersList,
  getMaritalStatusList,
  getStatesList,
  location,
  history
}) => {
  const steps = [
    "Personal",
    "Education",
    "Organization",
    "Address",
    "Attachments"
  ];
  const [activeStep, setActiveStep] = useState(0);
  const [isValid, setIsValid] = useState(false);
  const [isLoading, setisLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userDocuments, setUserDocuments] = useState([]);
  const [isDisclaimerChecked, setIsDisclaimerChecked] = useState(false);
  const [documentsLoading, setDocumentsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const personalForm = useRef(null);
  const qualificationForm = useRef(null);
  const organizationForm = useRef(null);
  const addressForm = useRef(null);
  const user = JSON.parse(window.sessionStorage.getItem("user"));
  const { state: { candidateId = "" } = {} } = location;

  // When user other than candidate tries to navigate directly to any other pages other than candidate list.
  if (!candidateId && user.roleId !== ROLES.CANDIDATE) {
    history.push("/CandidateList");
  }

  // When candidate user tries to navigate directly to Candidate Form in Onboarding phase.
  if (
    user.roleId === ROLES.CANDIDATE &&
    generalDetails &&
    generalDetails.status >= CANDIDATE_STATUS.ONBOARDING.id
  ) {
    history.push("/OnboardingForms", { candidateId });
  }

  useEffect(() => {
    resetCandidateDetails();
    getGendersList();
    getMaritalStatusList();
    getCandidateDetails(candidateId || user.id || "");
    getUserDocuments(candidateId || user.id || "");
    setisLoading(false);
  }, []);

  useEffect(() => {
    setIsValid(isValidForm());
  });
  const filterTempIDs = inputDataArray =>
    inputDataArray.map(inputData =>
      omitBy(
        inputData,
        (value, key) => key === "id" && value.toString().startsWith("temp_")
      )
    );

  const filterInValidRecords = inputDataArray =>
    inputDataArray.filter(inputData => inputData.organizationName);

  const getUserDocuments = async userId => {
    try {
      const res = await api.getUserDocuments(userId);
      if (res && !res.errorMessage) {
        const savedUserDocuments = res.map(file => {
          return new File([b64toBlob(file.byteData, file.type)], file.name, {
            type: file.type
          });
        });
        if (savedUserDocuments && savedUserDocuments.length)
          setUserDocuments(savedUserDocuments);
      }
    } catch (e) {}
    setDocumentsLoading(false);
  };
  const saveCandidateDetails = async (isSubmit = false) => {
    if (isSubmit) {
      setIsSubmitting(true);
    } else {
      setIsSaving(true);
    }
    const updatedQualificationDetails = filterTempIDs(qualificationDetails);
    const updatedOrganizationDetails = filterInValidRecords(
      filterTempIDs(organizationDetails)
    );
    const updatedAddressDetails = filterTempIDs(addressDetails);
    const candidateDetails = {
      ...generalDetails,
      updatedBy: user.id,
      userName: personalDetails.emailId, //Temporarily updating User Name with Email Id
      status: isSubmit
        ? CANDIDATE_STATUS.SUBMITTED.id
        : CANDIDATE_STATUS.INPROGRESS.id,
      userDetails: {
        ...personalDetails,
        panCardNo: encrypt(personalDetails.panCardNo),
        pfUanNo: encrypt(personalDetails.pfUanNo),
        adhaarNumber: encrypt(personalDetails.adhaarNumber),
        qualifications: updatedQualificationDetails,
        organizationDetails: updatedOrganizationDetails,
        addresses: updatedAddressDetails
      }
    };
    toast.configure();

    try {
      const res = await api.saveCandidateDetails(candidateDetails);
      let fileUploadResponse = null;
      if (activeStep === 4 || isSubmit) {
        fileUploadResponse = await uploadUserDocuments(userDocuments);
      }
      if (
        res &&
        !res.errorMessage &&
        (activeStep !== 4 ||
          (fileUploadResponse && !fileUploadResponse.errorMessage))
      ) {
        const successMessage = isSubmit
          ? "Successfully Submitted"
          : "Successfully Saved";
        toast.success(successMessage, defaultToastOptions);
        getCandidateDetails(generalDetails.id);
        if (isSubmit) history.push("/SubmitPage");
      } else {
        setIsSubmitting(false);

        toast.error(`Error while Saving Details`, defaultToastOptions);
      }
    } catch (e) {
      setIsSubmitting(false);

      toast.error(`Error while Saving Details`, defaultToastOptions);
    }
    setIsSaving(false);
  };

  const uploadUserDocuments = async userDocuments => {
    try {
      const formData = new FormData();
      for (var index = 0; index < userDocuments.length; index++) {
        if (userDocuments[index].type !== "application/pdf")
          var element = await imageCompressor(userDocuments[index]);
        else element = userDocuments[index];
        formData.append("files", element);
      }
      const res = await api.uploadDocuments(formData, personalDetails.userId);
      return res;
    } catch (e) {
      toast.error(`Error while Uploading Documents`, defaultToastOptions);
    }
  };
  const isValidForm = () => {
    switch (activeStep) {
      case 0:
        return personalForm.current && personalForm.current.checkValidity();
      case 1:
        return (
          qualificationForm.current && qualificationForm.current.checkValidity()
        );
      case 2:
        return (
          organizationForm.current && organizationForm.current.checkValidity()
        );
      case 3:
        return addressForm.current && addressForm.current.checkValidity();
      case 4:
        return userDocuments && userDocuments.length;
      default:
        return true;
    }
  };

  const isEmptyOrganizationForm = () => {
    let isEmptyOrganizationFormData = true;
    if (organizationDetails && organizationDetails.length) {
      if (organizationDetails.length === 1) {
        const {
          id = "",
          candidateId = "",
          lastOrganization,
          ...organizationFormData
        } = organizationDetails[0];
        isEmptyOrganizationFormData = !some(organizationFormData, val => val);
      } else {
        isEmptyOrganizationFormData = false;
      }
    }

    return isEmptyOrganizationFormData;
  };

  const getHelperText = () => {
    let helperText = "Please fill in all the mandatory fields.";
    return helperText;
  };

  return (
    <Fragment>
      {isLoading ? (
        <div>Loading...</div>
      ) : (
        <Fragment>
          <div>
            {" "}
            <h6 className="ml-3 pt-2">
              {user && user.roleId === 3 ? (
                activeStep !== 4 && getHelperText()
              ) : (
                <Link
                  className="text-decoration-none back-link"
                  to="/CandidateList"
                >
                  Back to Candidate List
                </Link>
              )}
            </h6>
            <HorizontalLinearStepper
              steps={steps}
              activeStep={activeStep}
              finished={false}
            />
            {activeStep === 0 && (
              <Form
                noValidate
                className="w-100 pt-3 ml-0 text-right"
                ref={personalForm}
              >
                <PersonalInfo
                  personalDetails={personalDetails}
                  genders={genders}
                  maritalStatusList={maritalStatusList}
                  setCandidatePersonalDetails={setCandidatePersonalDetails}
                />
              </Form>
            )}
            {activeStep === 1 && (
              <Form ref={qualificationForm}>
                <QualificationInfo
                  candidateId={personalDetails.id}
                  qualificationDetails={qualificationDetails}
                  updateCandidateQualificationDetails={
                    updateCandidateQualificationDetails
                  }
                  addCandidateQualificationDetails={
                    addCandidateQualificationDetails
                  }
                  deleteCandidateQualificationDetails={
                    deleteCandidateQualificationDetails
                  }
                />
              </Form>
            )}
            {activeStep === 2 && (
              <Form ref={organizationForm}>
                <OrganizationInfo
                  candidateId={personalDetails.id}
                  organizationDetails={organizationDetails}
                  updateCandidateOrganizationDetails={
                    updateCandidateOrganizationDetails
                  }
                  addCandidateOrganizationDetails={
                    addCandidateOrganizationDetails
                  }
                  deleteCandidateOrganizationDetails={
                    deleteCandidateOrganizationDetails
                  }
                />
              </Form>
            )}
            {activeStep === 3 && (
              <Form ref={addressForm}>
                <AddressInfo
                  candidateId={personalDetails.id}
                  addressDetails={addressDetails}
                  updateCandidateAddressDetails={updateCandidateAddressDetails}
                  addCandidateAddressDetails={addCandidateAddressDetails}
                  updateCandidateAddressDetailsBulk={
                    updateCandidateAddressDetailsBulk
                  }
                  getStatesList={getStatesList}
                  states={states}
                />
              </Form>
            )}
            {activeStep === 4 && (
              <Attachments
                userId={personalDetails.userId}
                cDKEmployeeType={personalDetails.cDKEmployeeType}
                userDocuments={userDocuments}
                setUserDocuments={setUserDocuments}
                documentsLoading={documentsLoading}
                setIsDisclaimerChecked={setIsDisclaimerChecked}
                isDisclaimerChecked={isDisclaimerChecked}
                selectedUploadDocTypes={personalDetails.selectedUploadDocTypes}
                setCandidatePersonalDetails={setCandidatePersonalDetails}
              />
            )}
            <Navbar className="footer" fixed="bottom">
              <Row className="w-100">
                <Col md="9">
                  {activeStep !== 0 && (
                    <Button
                      variant="outline-light"
                      onClick={() =>
                        setActiveStep(prevActiveStep => prevActiveStep - 1)
                      }
                    >
                      BACK TO {steps[activeStep - 1].toUpperCase()}
                    </Button>
                  )}
                </Col>

                <Col md="2" className="text-right">
                  <Button
                    className="btn-controls"
                    variant="outline-light"
                    onClick={() => saveCandidateDetails(false)}
                    disabled={!isValid}
                  >
                    SAVE INFO
                  </Button>
                </Col>

                <Col md="1" className="text-right">
                  {activeStep !== 4 ? (
                    <Button
                      className="btn-controls"
                      variant="outline-light"
                      onClick={() =>
                        setActiveStep(prevActiveStep => prevActiveStep + 1)
                      }
                      disabled={
                        activeStep === 2
                          ? !isEmptyOrganizationForm() && !isValid
                          : !isValid
                      }
                    >
                      NEXT
                    </Button>
                  ) : (
                    <Button
                      className="btn-controls"
                      variant="outline-light"
                      onClick={() => saveCandidateDetails(true)}
                      disabled={!isDisclaimerChecked || !isValid}
                    >
                      SUBMIT
                    </Button>
                  )}
                </Col>
              </Row>
            </Navbar>
            <PageSpinner show={isSubmitting || isSaving} />
          </div>
        </Fragment>
      )}
    </Fragment>
  );
};
const mapStateToProps = ({ candidateDetails }) => ({
  generalDetails: candidateDetails.generalDetails || {},
  personalDetails: candidateDetails.personalDetails || {},
  qualificationDetails: candidateDetails.qualificationDetails || [],
  addressDetails: candidateDetails.addressDetails || [],
  organizationDetails: candidateDetails.organizationDetails || [],
  genders: candidateDetails.genders || [],
  maritalStatusList: candidateDetails.maritalStatusList || [],
  states: candidateDetails.states || []
});
const mapDispatchToProps = {
  getCandidateDetails: candidateActions.getCandidateDetails,
  resetCandidateDetails: candidateActions.resetCandidateDetails,
  setCandidatePersonalDetails: candidateActions.setCandidatePersonalDetails,
  updateCandidateQualificationDetails:
    candidateActions.updateCandidateQualificationDetails,
  addCandidateQualificationDetails:
    candidateActions.addCandidateQualificationDetails,
  deleteCandidateQualificationDetails:
    candidateActions.deleteCandidateQualificationDetails,
  updateCandidateAddressDetails: candidateActions.updateCandidateAddressDetails,
  addCandidateAddressDetails: candidateActions.addCandidateAddressDetails,
  updateCandidateAddressDetailsBulk:
    candidateActions.updateCandidateAddressDetailsBulk,
  updateCandidateOrganizationDetails:
    candidateActions.updateCandidateOrganizationDetails,
  addCandidateOrganizationDetails:
    candidateActions.addCandidateOrganizationDetails,
  deleteCandidateOrganizationDetails:
    candidateActions.deleteCandidateOrganizationDetails,
  getGendersList: candidateActions.getGendersList,
  getMaritalStatusList: candidateActions.getMaritalStatusList,
  getStatesList: candidateActions.getStatesList
};
export default connect(mapStateToProps, mapDispatchToProps)(CandidateForm);
